import { useCallback, useEffect, useSyncExternalStore } from 'react'

import { useSignalLog } from '@/app/lib/signalLog'
import { LIVE_LIST_POLL_MS, livePollDelay } from '@/app/lib/quant/livePoll'
import { observeLiveSession, type LiveEmit } from '@/app/lib/quant/liveObserver'
import { quantApi } from '@/app/lib/quant/quantApi'
import { quantErrorMessage } from '@/app/lib/quant/quantErrors'
import { isActive } from '@/app/lib/quant/liveView'
import type { LiveSession } from '@/app/lib/quant/types'
import { registerSessionReset } from '@/app/lib/sessionReset'

export { LIVE_LIST_POLL_MS }

export type QuantLiveListState =
  | { status: 'loading' }
  /** `error` is set while the latest read failed; the previous answer stays on screen. */
  | {
      status: 'loaded'
      sessions: LiveSession[]
      active: number
      max: number
      ownActive: number
      receivedAt: number
      error?: string
    }
  | { status: 'error'; message: string }

/*
 * Spec 041: ONE module-level store for `GET /quant/live`, read by the whole
 * Strategies screen (header stat, tab count, list badge, Live pane). It reads at
 * the first subscribe, then every 10 s only while a session is active or the Live
 * pane is open; nothing is requested while the tab is hidden and a read happens at
 * once when it becomes visible. After a failure the delay backs off 2, 4, 8, 15 s
 * and the last good answer stays. Timers are `setTimeout` so `page.clock` drives them.
 */
let state: QuantLiveListState = { status: 'loading' }
let sequence = 0
let failures = 0
let timer: ReturnType<typeof setTimeout> | null = null
let controller: AbortController | null = null
let inFlight = false
let pending = false
let queued = false
let wantRead = false
let paneOpenCount = 0
let emitLive: LiveEmit | null = null
const listeners = new Set<() => void>()

function publish(next: QuantLiveListState): void {
  state = next
  for (const listener of listeners) listener()
}

function clearTimer(): void {
  if (timer !== null) clearTimeout(timer)
  timer = null
}

const isVisible = (): boolean => typeof document === 'undefined' || document.visibilityState !== 'hidden'
const hasActive = (): boolean => state.status === 'loaded' && state.sessions.some(isActive)

function schedule(): void {
  clearTimer()
  if (listeners.size === 0) return
  const delay = livePollDelay({ visible: isVisible(), paneOpen: paneOpenCount > 0, hasActive: hasActive(), failures })
  if (delay === null) return
  timer = setTimeout(() => {
    timer = null
    load()
  }, delay)
}

function settle(): void {
  if (pending) {
    pending = false
    load()
    return
  }
  schedule()
}

function load(): void {
  if (inFlight) {
    pending = true
    return
  }
  clearTimer()
  inFlight = true
  sequence += 1
  const mine = sequence
  const own = new AbortController()
  controller = own
  quantApi.listLiveSessions(own.signal).then(
    (envelope) => {
      if (mine !== sequence || own.signal.aborted) return
      inFlight = false
      failures = 0
      const sessions = Array.isArray(envelope.sessions) ? envelope.sessions : []
      const activeHere = sessions.filter(isActive).length
      publish({
        status: 'loaded',
        sessions,
        active: typeof envelope.active === 'number' ? envelope.active : activeHere,
        max: typeof envelope.max === 'number' ? envelope.max : 0,
        ownActive: typeof envelope.own_active === 'number' ? envelope.own_active : activeHere,
        receivedAt: Date.now(),
      })
      if (emitLive !== null) for (const session of sessions) observeLiveSession(session, emitLive)
      settle()
    },
    (error: unknown) => {
      if (mine !== sequence || own.signal.aborted) return
      inFlight = false
      failures += 1
      const message = quantErrorMessage(error)
      publish(state.status === 'loaded' ? { ...state, error: message } : { status: 'error', message })
      settle()
    },
  )
}

/** Reads now (after a start, stop or delete, or a Retry). Safe to call from anywhere. */
export function refreshLiveSessions(): void {
  load()
}

/** Replaces or inserts one session; the counts move by the difference until the next read. */
export function mergeLiveSession(session: LiveSession): void {
  if (state.status !== 'loaded') return
  const existing = state.sessions.find((s) => s.id === session.id)
  const delta = (isActive(session) ? 1 : 0) - (existing !== undefined && isActive(existing) ? 1 : 0)
  const sessions = existing === undefined ? [session, ...state.sessions] : state.sessions.map((s) => (s.id === session.id ? session : s))
  publish({ ...state, sessions, active: Math.max(0, state.active + delta), ownActive: Math.max(0, state.ownActive + delta) })
  schedule()
}

/** Drops one session from the held answer (after a Delete). */
export function removeLiveSession(id: string): void {
  if (state.status !== 'loaded') return
  const existing = state.sessions.find((s) => s.id === id)
  if (existing === undefined) return
  const delta = isActive(existing) ? 1 : 0
  publish({
    ...state,
    sessions: state.sessions.filter((s) => s.id !== id),
    active: Math.max(0, state.active - delta),
    ownActive: Math.max(0, state.ownActive - delta),
  })
  schedule()
}

function onVisibility(): void {
  if (listeners.size === 0) return
  clearTimer()
  if (!isVisible()) return
  if (livePollDelay({ visible: true, paneOpen: paneOpenCount > 0, hasActive: hasActive(), failures }) !== null) load()
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener)
  if (listeners.size === 1) document.addEventListener('visibilitychange', onVisibility)
  wantRead = wantRead || listeners.size === 1 || state.status !== 'loaded'
  if (!queued) {
    queued = true
    // Defer so subscribe itself never publishes synchronously during React's commit.
    queueMicrotask(() => {
      queued = false
      const read = wantRead
      wantRead = false
      if (listeners.size > 0 && read && isVisible()) load()
    })
  }
  return () => {
    listeners.delete(listener)
    if (listeners.size === 0) {
      document.removeEventListener('visibilitychange', onVisibility)
      clearTimer()
      sequence += 1 // drop any in-flight answer
      controller?.abort()
      controller = null
      inFlight = false
      pending = false
    }
  }
}

function getSnapshot(): QuantLiveListState {
  return state
}

function paneChanged(): void {
  if (listeners.size === 0) return
  // Opening the pane with no cadence running and nothing in flight: the held answer may be old, read now.
  if (paneOpenCount > 0 && state.status === 'loaded' && timer === null && !inFlight && !queued && isVisible()) {
    load()
    return
  }
  if (timer === null || paneOpenCount === 0) schedule()
}

/** The Live pane calls this while mounted: it turns the 10 s cadence on. */
export function useLivePaneOpen(open: boolean): void {
  useEffect(() => {
    if (!open) return
    paneOpenCount += 1
    paneChanged()
    return () => {
      paneOpenCount -= 1
      paneChanged()
    }
  }, [open])
}

export function resetQuantLiveSessions(): void {
  clearTimer()
  controller?.abort()
  controller = null
  sequence += 1
  failures = 0
  inFlight = false
  pending = false
  queued = false
  wantRead = false
  paneOpenCount = 0
  emitLive = null
  document.removeEventListener('visibilitychange', onVisibility)
  listeners.clear()
  state = { status: 'loading' }
}

export function useQuantLiveSessions(): QuantLiveListState & { retry: () => void } {
  const { emit } = useSignalLog()
  useEffect(() => {
    emitLive = emit
  }, [emit])
  const current = useSyncExternalStore(subscribe, getSnapshot)
  const retry = useCallback(() => {
    failures = 0
    if (state.status === 'error') publish({ status: 'loading' })
    load()
  }, [])
  return { ...current, retry }
}

registerSessionReset(resetQuantLiveSessions)
